<script setup>
  import { onMounted, ref, useTemplateRef } from 'vue'
  import Swiper from 'swiper'
  import { Autoplay, Pagination } from 'swiper/modules'

  import 'swiper/css';
  Swiper.use([Autoplay, Pagination])

  const props = defineProps({
    theme: {
      type: String,
      default: 'default',
    },
    navigation: {
      type: String,
      default: 'false'
    },
    pagination: {
      type: String,
      default: 'false'
    }
  })

  const swiper = ref(null)
  const swiperContainer = useTemplateRef('swiperContainer')
  const paginationElement = useTemplateRef('paginationElement')
  const emit = defineEmits(['handleSlideChange'])

  onMounted(() => {
    const options = {
      default: {
        slidesPerView: 1,
        spaceBetween: 0,
        keyboard: {
          enabled: true,
          onlyInViewport: true
        }
      },
      author: {
        slidesPerView: 1.2,
        spaceBetween: 26,
        keyboard: {
          enabled: true,
          onlyInViewport: true
        }
      },
      count: {
        slidesPerView: 1,
        spaceBetween: 16,
        pagination: {
          el: paginationElement.value,
          clickable: true
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true
        }
      },
      filter: {
        slidesPerView: 2.1,
        spaceBetween: 32,
        freeMode: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true
        },
        breakpoints: {
          991: {
            slidesPerView: 'auto',
            spaceBetween: 64
          },
        }
      },
      hero: {
        direction: 'vertical',
        slidesPerView: 1,
        touchReleaseOnEdges: true,
        autoplay: {
          delay: 10000
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true
        }
      },
      quotes: {
        slidesPerView: 1,
        spaceBetween: 0,
        pagination: {
          el: paginationElement.value,
          clickable: true
        }
      },
      teaser: {
        slidesPerView: 1.1,
        spaceBetween: 16,
      },
      timelineNavigation: {
        slidesPerView: 3.5,
        spaceBetween: 20,
        centeredSlides: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true
        },
        breakpoints: {
          991: {
            slidesPerView: 6.3,
            centeredSlides: false
          },
          1200: {
            slidesPerView: 12,
            centeredSlides: false
          }
        }
      },
      timeline: {
        slidesPerView: 1,
        keyboard: {
          enabled: true,
          onlyInViewport: true
        }
      }
    }

    // Init swiper
    swiper.value = new Swiper(swiperContainer.value, options[props.theme])
    swiper.value.on('slideChange', (swiper) => {
      emit('handleSlideChange', swiper.realIndex)
    })
  })

  defineExpose({
    swiper
  })

</script>

<template>
  <div
    class="swiper-content-container relative w-full h-full"
    :class="theme"
  >
    <div
      class="swiper h-full w-full"
      ref="swiperContainer"
    >
      <div class="swiper-wrapper">
        <slot
          name="slides"
        />
      </div>
    </div>
    <div
      v-if="pagination"
      ref="paginationElement"
      class="swiper-pagination"
      :class="theme"
    />
  </div>
</template>

<style lang="scss">
  .swiper-content-container {
    &.hero {
      @apply w-full h-full;
      .swiper {
        @apply h-full w-full;
      }
    }
    &.timelineNavigation {
      @apply max-w-screen-2xl mx-auto px-6;
    }
    &.teaser, &.author, &.count {
      .swiper-slide {
        @apply opacity-60 transition-opacity duration-300 ease-in-out;
        &.swiper-slide-active {
          @apply opacity-100;
        }
      }
    }
  }
  .swiper-pagination {
    &.quotes, &.count {
      @apply relative lg:absolute lg:bottom-16 lg:left-20 flex flex-row justify-between lg:justify-start items-center lg:gap-3 z-50 w-full;
      &:before {
        content: '';
        @apply absolute w-full h-[1px] scale-x-125 bg-headline opacity-10 lg:hidden;
      }
      .swiper-pagination-bullet {
        @apply w-5 h-5 rounded-full relative bg-transparent flex items-center justify-center cursor-pointer;
        &:before {
          content: '';
          @apply w-2 h-2 bg-headline rounded-full opacity-60;
        }
        &:after {
          content: '';
          @apply w-full h-full absolute opacity-0 rounded-full border border-headline transition-all duration-300 ease-in-out;
        }
        &.swiper-pagination-bullet-active {
          &:after, &:before {
            @apply opacity-100;
          }
        }
      }
    }
    &.count {
      &:before {
        @apply bg-white opacity-100 scale-x-150;
      }
      .swiper-pagination-bullet {
        &:before {
          @apply bg-white;
        }
        &:after {
          @apply border-white;
        }
      }
    }
  }
</style>
